import { Col, Modal, Row, Table, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCreateMapping } from '../../../../utils/api/mapping/useCreateMapping';
import { useMapProducts } from '../../../../utils/api/mapping/useMapProducts';
import { useDownloadCSV } from '../../../../utils/api/mpCatalogue/useDownloadCSV';
import { useGetMpCatalogueAssociations } from '../../../../utils/api/mpCatalogue/useGetMpCatalogueAssociation';
import {
  useMappingDetails,
  useMappingFilters,
} from '../../../../utils/zustand';
import { getMappingColumns } from './columns';
import MappingFilters from './MappingFilters';
import PreviewDrawer from '../preview/PreviewDrawer';
import { usePreviewMapping } from '../../../../utils/api/mapping/usePreviewMapping';
import { useGetMarketplaces } from '../../../../utils/api/marketplaces/useGetMarketplaces';

const MappingList = () => {
  const [mappingId, setMappingId] = useState();

  const { filters, setMarketplaceFilter, setStoreFilter, setMPCategoryFilter } =
    useMappingFilters();
  const { mappingDetails, setMappingDetails } = useMappingDetails();

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const { downloadCSV } = useDownloadCSV();
  const { mapProducts } = useMapProducts();
  const { createMapping, isLoading: isCreatingMapping } = useCreateMapping();

  const navigate = useNavigate();

  const {
    mpCatalogueAssociations,
    isLoading: isMpCatalogueAssociationsLoading,
  } = useGetMpCatalogueAssociations({
    mp_category: filters.mpCategory,
    store: filters.store,
    store__marketplace__name: filters.marketplace,
  });

  const { marketplaces } = useGetMarketplaces({ is_active: true });

  const filteredAssociations = useMemo(() => {
    if (!mpCatalogueAssociations) return [];
    return mpCatalogueAssociations.filter((assoc) =>
      marketplaces
        .map((m) => m.name)
        .includes(assoc?.mp_category?.marketplace?.name)
    );
  }, [mpCatalogueAssociations]);

  const {
    previewMapping,
    isLoading: isPreviewLoading,
    error: previewError,
  } = usePreviewMapping();

  const handleTransformAll = (e, record) => {
    e.domEvent?.stopPropagation();
    Modal.confirm({
      title: 'Lancer la transformation',
      icon: <ExclamationCircleOutlined />,
      content: `Voulez-vous transformer tous les produits de la catégorie "${record?.mp_category?.name || ''}" ?`,
      okText: 'Transformer',
      cancelText: 'Annuler',
      onOk: async () => {
        const marketplaceId = record?.mp_category?.marketplace?.id;
        const mp_categories = [record?.mp_category?.id];
        const stores = [record?.store?.id];
        const body = { mp_categories, stores };
        try {
          await mapProducts({
            marketplaceId,
            body,
          });
          notification.success({
            message: 'Transformation lancée',
            description:
              'La transformation des produits a été lancée avec succès.',
            placement: 'bottomRight',
            duration: 3,
          });
        } catch (error) {
          notification.error({
            message: 'Erreur de transformation',
            description:
              error?.response?.data?.detail ||
              'Une erreur est survenue lors de la transformation des produits.',
            placement: 'bottomRight',
            duration: 3,
          });
        }
      },
    });
  };

  const handleNavigateToMapping = async (record) => {
    if (isCreatingMapping) return;

    if (record.mapping_id) {
      navigate(`/dashboard/mapping/${record.mapping_id}`);
      return;
    }

    try {
      const response = await createMapping({
        name: `${record.store?.name} - ${record.mp_category?.name}`,
        store_id: record.store?.id,
        mp_category_id: record.mp_category?.id,
        status: 'draft',
        transformations: {},
      });
      navigate(`/dashboard/mapping/${response.id}`);
    } catch (error) {
      notification.error({
        message: 'Erreur',
        description:
          error?.response?.data?.message ||
          'Impossible de créer le mapping. Veuillez réessayer.',
        placement: 'bottomRight',
      });
    }
  };

  const handleManageMapping = (e, record) => {
    e.domEvent?.stopPropagation();
    handleNavigateToMapping(record);
  };

  const handlePreviewMapping = async (e, record) => {
    e.domEvent?.stopPropagation();
    try {
      setMappingId(record.mapping_id);
      const data = await previewMapping({ mappingId: record.mapping_id });

      setMappingDetails(record);

      setPreviewData(data);
      setPreviewOpen(true);
    } catch (error) {
      notification.error({
        message: 'Erreur de prévisualisation',
        description: error?.response?.data?.detail || 'Une erreur est survenue',
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleExportCSV = async (e, record) => {
    e.domEvent?.stopPropagation();
    const body = {
      mp_category: record?.mp_category?.id,
      store: record?.store?.id,
      marketplace: record?.mp_category?.marketplace?.id,
    };
    try {
      const result = await downloadCSV(body);
      if (result.async) {
        notification.success({
          message: 'Export en cours',
          description: "Vous recevrez un email lorsque l'export sera terminé.",
          placement: 'bottomRight',
          duration: 5,
        });
      } else {
        notification.success({
          message: 'Export réussi',
          description: result.success,
          placement: 'bottomRight',
          duration: 3,
        });
        if (result.exportError) {
          notification.warning({
            message: 'Attention',
            description: result.exportError,
            placement: 'bottomRight',
            duration: 5,
          });
        }
      }
    } catch (error) {
      notification.error({
        message: "Erreur d'export",
        description: error.message,
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleBroadcastProducts = async (e, record) => {
    e.domEvent?.stopPropagation();
    console.log('test diffusion');
    console.log(record);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewData(null);
  };

  const onReferenceChange = async (reference) => {
    if (!mappingId) return;
    try {
      const data = await previewMapping({ mappingId, product: reference });
      setPreviewData(data);
    } catch (error) {
      notification.error({
        message: 'Erreur de prévisualisation',
        description: error?.response?.data?.detail || 'Une erreur est survenue',
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const columns = getMappingColumns({
    onTransformAll: handleTransformAll,
    onManageMapping: handleManageMapping,
    onPreviewMapping: handlePreviewMapping,
    onExportCSV: handleExportCSV,
    onBroadcastProducts: handleBroadcastProducts,
  });

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <h3 style={{ margin: 0 }}>Mapping</h3>
        </Col>
        <Col span={24}>
          <MappingFilters
            selectedMarketplace={filters.marketplace}
            setSelectedMarketplace={setMarketplaceFilter}
            selectedStore={filters.store}
            setSelectedStore={setStoreFilter}
            selectedCategory={filters.mpCategory}
            setSelectedCategory={setMPCategoryFilter}
          />
        </Col>
        <Col span={24}>
          <Table
            columns={columns}
            dataSource={filteredAssociations}
            rowKey={(record) => `${record.store?.id}-${record.mp_category?.id}`}
            loading={isMpCatalogueAssociationsLoading || isCreatingMapping}
            pagination={{ size: 'small' }}
            scroll={{ y: 'calc(100vh - 320px)' }}
            onRow={(record) => ({
              onClick: () => handleNavigateToMapping(record),
            })}
            style={{ cursor: 'pointer' }}
          />
        </Col>
      </Row>
      <PreviewDrawer
        open={previewOpen}
        onClose={handleClosePreview}
        onReferenceChange={onReferenceChange}
        store={mappingDetails?.store}
        mp_category={mappingDetails?.mp_category}
        data={previewData}
        isLoading={isPreviewLoading}
        error={previewError}
      />
    </>
  );
};

export default MappingList;
